import _ from "lodash";
import type { FieldValues, UseFormSetValue } from "react-hook-form";

import {
  isValidTimeString,
  MAIN_TIME_FORMAT,
  NO_VALUE_DISPLAY,
  parseHHmmTimeToMinutes,
  type IDropdownItem,
  type TTimeHHmm,
} from "@ryde/shared";

import type { IRatioItem, IRatioSortedEntry } from "./table-ratios.models";

export const normalizeTableRatiosFieldData = (
  watchedList: unknown,
  fieldName: string,
  setValue: UseFormSetValue<FieldValues>,
): IRatioItem[] => {
  if (watchedList && !_.isArray(watchedList)) {
    /* guard for invalid value coming from backend*/
    setValue(fieldName, []);
    return [];
  }

  return (watchedList || []) as IRatioItem[];
};

/**
 * Sort by destination area name for display while carrying the index each row holds in the form
 * array. Editing and removing address that index, so passing the sorted position instead would
 * rewrite a different row and make a row look like a duplicate of itself.
 */
export const buildSortedRatioEntries = ({
  ratios,
  areaItems,
}: {
  ratios: IRatioItem[];
  areaItems: IDropdownItem[];
}): IRatioSortedEntry[] => {
  const getAreaLabel = (areaId: number | undefined): string =>
    areaItems.find(item => Number(item.value) === Number(areaId))?.label ||
    NO_VALUE_DISPLAY;

  return ratios
    .map((item, formIndex) => ({ item, formIndex }))
    .toSorted((a, b) =>
      getAreaLabel(a.item.areaId).localeCompare(getAreaLabel(b.item.areaId)),
    );
};

/**
 * The save-time checks the controls cannot make themselves, as the translation key to show.
 *
 * `SelectBoundInputByControl` renders a required marker but registers no rule, so the area is invisible
 * to `formState.isValid`. The time pickers do register `required`, but a time they could not parse
 * reaches the form as the literal string "Invalid Date", which satisfies `required` and would be stored
 * as a window the backend can never match.
 */
export const getRatioRowErrorKey = (row: IRatioItem): string | undefined => {
  if (!row.ratio || Number(row.ratio) <= 0) {
    return "errors.invalidNumber";
  }

  if (!row.areaId) {
    return "errors.requiredField";
  }

  if (
    !isValidTimeString(row.from, MAIN_TIME_FORMAT) ||
    !isValidTimeString(row.to, MAIN_TIME_FORMAT)
  ) {
    return "errors.invalidTimeFormat";
  }

  return undefined;
};

/**
 * True when the window closes earlier in the day than it opens, e.g. 20:00 -> 06:00.
 *
 * That is a legitimate overnight window, not a mistake: it is how the night price window is already
 * configured (`calculateNightAdditions` treats an end before the start as spanning midnight), and 20
 * of the ratio windows in production are written that way. The row only needs to say so on screen.
 */
export const isOvernightRatioWindow = ({
  from,
  to,
}: Pick<IRatioItem, "from" | "to">): boolean => {
  if (
    !isValidTimeString(from, MAIN_TIME_FORMAT) ||
    !isValidTimeString(to, MAIN_TIME_FORMAT)
  ) {
    return false;
  }

  // Both bounds parsed above, so neither can still be the empty member of the union.
  return (
    parseHHmmTimeToMinutes(to as TTimeHHmm) <
    parseHHmmTimeToMinutes(from as TTimeHHmm)
  );
};
